import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { useParams } from 'react-router';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GetExpenseDocument,
  GetExpenseQuery,
  GetExpenseQueryVariables,
  UpdateExpenseDocument,
  UpdateExpenseMutation,
  UpdateExpenseMutationVariables,
  DeleteExpenseDocument,
  DeleteExpenseMutation,
  DeleteExpenseMutationVariables,
  UndeleteExpenseDocument,
  UndeleteExpenseMutation,
  UndeleteExpenseMutationVariables,
  MarkExpensePaidDocument,
  MarkExpensePaidMutation,
  MarkExpensePaidMutationVariables,
  MarkExpenseUnpaidDocument,
  MarkExpenseUnpaidMutation,
  MarkExpenseUnpaidMutationVariables,
  AddExpenseCommentDocument,
  AddExpenseCommentMutation,
  AddExpenseCommentMutationVariables,
  UpdateExpenseCommentDocument,
  UpdateExpenseCommentMutation,
  UpdateExpenseCommentMutationVariables,
  RemoveExpenseCommentDocument,
  RemoveExpenseCommentMutation,
  RemoveExpenseCommentMutationVariables,
  DeleteExpensePaymentDocument,
  DeleteExpensePaymentMutation,
  DeleteExpensePaymentMutationVariables,
  SetExpenseUnitAssignmentsDocument,
  SetExpenseUnitAssignmentsMutation,
  SetExpenseUnitAssignmentsMutationVariables,
  GetUnitsDocument,
  GetUnitsQuery,
  GetUnitsQueryVariables,
} from '../../generated/graphql';
import { FormInput } from '../../components/form-input';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../components/app-context';
import { CommentList } from '../../components/comments-list';
import { InputComment } from '../../components/input-comment';
import { BasePage } from '../../components/base-page';
import { appPaths } from '../../app-paths';
import { ConfirmationDialog } from '../../components/confirmation-dialog';
import { MoneyTransactionDialog } from '../money-transaction-list/components/money-transaction-dialog';
import { DeletedItemBanner } from '../../components/deleted-item-banner';
import { ExpensePaymentList } from '../../components/expense-payment-list';
import { AddIcon, RemoveItemIcon } from '../../components/icons';

interface FormFields {
  title: string;
  amount: string;
  taxDeductible: string;
  date: string;
  dueDate: string;
  refNo: string;
}

interface UnitAssignmentRow {
  unitId: string;
  amount: string;
}

export function ExpenseDetailPage() {
  const app = useApp();
  const { show } = useAlert();
  const { expenseId } = useParams();

  const [formData, setFormData] = useState<FormFields>({
    title: '',
    amount: '',
    taxDeductible: '',
    date: '',
    dueDate: '',
    refNo: '',
  });

  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentToDelete, setPaymentToDelete] = useState<string | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  // Unit assignments editing
  const [assignmentRows, setAssignmentRows] = useState<UnitAssignmentRow[]>([]);
  const [assignmentsEdited, setAssignmentsEdited] = useState(false);

  const [fetchExpense, { data, loading: initialLoading, error, refetch }] = useLazyQuery<
    GetExpenseQuery,
    GetExpenseQueryVariables
  >(GetExpenseDocument);

  const { data: unitsData } = useQuery<GetUnitsQuery, GetUnitsQueryVariables>(GetUnitsDocument, {
    variables: { includeArchived: false },
  });
  const availableUnits = unitsData?.getUnits?.filter((u) => u.status !== 'archived') ?? [];

  useEffect(() => {
    if (expenseId) {
      fetchExpense({ variables: { id: expenseId } });
    }
  }, [expenseId, fetchExpense]);

  const expense = data?.getExpense;

  useEffect(() => {
    if (expense) {
      setFormData({
        title: expense.title || '',
        amount: expense.amount?.toString() || '',
        taxDeductible: expense.taxDeductible?.toString() || '',
        date: expense.date ? new Date(expense.date).toISOString().substring(0, 10) : '',
        dueDate: expense.dueDate ? new Date(expense.dueDate).toISOString().substring(0, 10) : '',
        refNo: expense.refNo || '',
      });
      // Initialise assignment rows from fetched data
      if (expense.unitAssignments?.length) {
        setAssignmentRows(
          expense.unitAssignments.map((a) => ({
            unitId: a.unitId,
            amount: a.amount.toString(),
          })),
        );
      } else {
        setAssignmentRows([]);
      }
      setAssignmentsEdited(false);
    }
  }, [expense]);

  const [updateExpense, { loading: updateLoading }] = useMutation<UpdateExpenseMutation, UpdateExpenseMutationVariables>(
    UpdateExpenseDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa upraviť výdavok.', e.message),
      onCompleted: () => {
        show.info('Výdavok bol úspešne upravený.');
        refetch?.();
      },
    },
  );

  const [deleteExpense, { loading: deleteLoading }] = useMutation<DeleteExpenseMutation, DeleteExpenseMutationVariables>(
    DeleteExpenseDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa zmazať výdavok.', e.message),
      onCompleted: () => {
        show.info('Výdavok bol úspešne zmazaný.');
        refetch?.();
      },
    },
  );

  const [undeleteExpense, { loading: undeleteLoading }] = useMutation<
    UndeleteExpenseMutation,
    UndeleteExpenseMutationVariables
  >(UndeleteExpenseDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa obnoviť výdavok.', e.message),
    onCompleted: () => {
      show.info('Výdavok bol úspešne obnovený.');
      refetch?.();
    },
  });

  const [markPaid, { loading: markPaidLoading }] = useMutation<MarkExpensePaidMutation, MarkExpensePaidMutationVariables>(
    MarkExpensePaidDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa označiť výdavok ako zaplatený.', e.message),
      onCompleted: () => {
        show.info('Výdavok bol označený ako zaplatený.');
        setShowMarkPaidDialog(false);
        refetch?.();
      },
    },
  );

  const [markUnpaid, { loading: markUnpaidLoading }] = useMutation<
    MarkExpenseUnpaidMutation,
    MarkExpenseUnpaidMutationVariables
  >(MarkExpenseUnpaidDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa označiť výdavok ako nezaplatený.', e.message),
    onCompleted: () => {
      show.info('Výdavok bol označený ako nezaplatený.');
      refetch?.();
    },
  });

  const [addComment] = useMutation<AddExpenseCommentMutation, AddExpenseCommentMutationVariables>(
    AddExpenseCommentDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa pridať komentár.', e.message),
      onCompleted: () => refetch?.(),
    },
  );

  const [updateComment] = useMutation<UpdateExpenseCommentMutation, UpdateExpenseCommentMutationVariables>(
    UpdateExpenseCommentDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa aktualizovať komentár.', e.message),
      onCompleted: () => refetch?.(),
    },
  );

  const [removeComment] = useMutation<RemoveExpenseCommentMutation, RemoveExpenseCommentMutationVariables>(
    RemoveExpenseCommentDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa zmazať komentár.', e.message),
      onCompleted: () => refetch?.(),
    },
  );

  const [deleteExpensePayment] = useMutation<DeleteExpensePaymentMutation, DeleteExpensePaymentMutationVariables>(
    DeleteExpensePaymentDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa odstrániť úhradu.', e.message),
      onCompleted: () => {
        show.info('Úhrada bola odstránená.');
        refetch?.();
      },
    },
  );

  const [setUnitAssignments, { loading: assignmentsLoading }] = useMutation<
    SetExpenseUnitAssignmentsMutation,
    SetExpenseUnitAssignmentsMutationVariables
  >(SetExpenseUnitAssignmentsDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa uložiť priradenia k nehnuteľnostiam.', e.message),
    onCompleted: () => {
      show.info('Priradenia k nehnuteľnostiam boli úspešne uložené.');
      setAssignmentsEdited(false);
      refetch?.();
    },
  });

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!expense) return;
    await updateExpense({
      variables: {
        expenseId: expense.id,
        input: {
          title: formData.title,
          amount: parseFloat(formData.amount),
          taxDeductible: parseFloat(formData.taxDeductible),
          date: formData.date ? new Date(formData.date).toISOString() : undefined,
          dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : undefined,
          refNo: formData.refNo || undefined,
        },
      },
    });
  };

  const handleDelete = async () => {
    if (!expense) return;
    if (expense.deletedAt) {
      await undeleteExpense({ variables: { expenseId: expense.id } });
    } else {
      await deleteExpense({ variables: { expenseId: expense.id } });
    }
  };

  const handleRemovePayment = async (paymentId: string) => {
    setPaymentToDelete(paymentId);
  };

  const confirmRemovePayment = async () => {
    if (!paymentToDelete) return;
    await deleteExpensePayment({ variables: { id: paymentToDelete } });
    setPaymentToDelete(null);
  };

  // Unit assignment helpers
  const assignmentTotal = assignmentRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const expenseAmount = parseFloat(formData.amount) || expense?.amount || 0;
  const expenseTaxDeductible = parseFloat(formData.taxDeductible) || expense?.taxDeductible || 0;
  const assignmentTotalMatchesExpense = Math.abs(assignmentTotal - expenseAmount) < 0.001;

  /** Preview: calculate proportional tax deductible per row (with rounding correction on first) */
  const previewTaxDeductible = (idx: number): number => {
    const amount = parseFloat(assignmentRows[idx]?.amount) || 0;
    if (expenseAmount <= 0 || amount <= 0) return 0;
    const raw = (amount / expenseAmount) * expenseTaxDeductible;
    let td = Math.round(raw * 100) / 100;
    // Apply rounding correction to first row
    if (idx === 0) {
      const sumOthers = assignmentRows
        .slice(1)
        .reduce((s, r) => {
          const a = parseFloat(r.amount) || 0;
          const rawOther = expenseAmount > 0 ? (a / expenseAmount) * expenseTaxDeductible : 0;
          return s + Math.round(rawOther * 100) / 100;
        }, 0);
      const diff = Math.round((expenseTaxDeductible - sumOthers - td) * 100) / 100;
      td = Math.round((td + diff) * 100) / 100;
    }
    return td;
  };

  const handleAddAssignmentRow = () => {
    setAssignmentRows((prev) => [...prev, { unitId: '', amount: '' }]);
    setAssignmentsEdited(true);
  };

  const handleRemoveAssignmentRow = (idx: number) => {
    setAssignmentRows((prev) => prev.filter((_, i) => i !== idx));
    setAssignmentsEdited(true);
  };

  const handleAssignmentChange = (idx: number, field: 'unitId' | 'amount', value: string) => {
    setAssignmentRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    setAssignmentsEdited(true);
  };

  const handleSaveAssignments = async () => {
    if (!expense) return;
    if (assignmentRows.length > 0 && !assignmentTotalMatchesExpense) {
      show.error(
        `Súčet priradených súm (${app.formatNumber(assignmentTotal, 2)} €) sa musí rovnať celkovej sume výdavku (${app.formatNumber(expenseAmount, 2)} €).`,
      );
      return;
    }
    const validRows = assignmentRows.filter((r) => r.unitId && parseFloat(r.amount) > 0);
    await setUnitAssignments({
      variables: {
        expenseId: expense.id,
        assignments: validRows.map((r) => ({ unitId: r.unitId, amount: parseFloat(r.amount) })),
      },
    });
  };

  if (!expense) {
    return (
      <BasePage
        title="Výdavok"
        loading={initialLoading}
        errorMsg={error ? 'Nepodarilo sa načítať detaily výdavku.' : undefined}
      >
        <div />
      </BasePage>
    );
  }

  const isPaid = !!expense.paidAt;
  const isDeleted = !!expense.deletedAt;
  const canEdit = !isPaid && !isDeleted;
  const loading = updateLoading || deleteLoading || undeleteLoading || markPaidLoading || markUnpaidLoading;
  const isLocked = !!expense.deletedAt || loading;

  return (
    <BasePage title={`Výdavok: ${expense.title}`}>
      {isDeleted && (
        <DeletedItemBanner
          text={`Tento výdavok bol zmazaný ${app.formatDate(expense.deletedAt)}.`}
          undeleteLabel="Obnoviť"
          onUndelete={handleDelete}
        />
      )}

      <Grid container spacing={3}>
        {/* Left side - Expense details */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={3}>
            <Box component="form" noValidate>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <FormInput
                    name="title"
                    label="Názov výdavku"
                    value={formData.title}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormInput
                    name="amount"
                    label="Suma (€)"
                    type="number"
                    value={formData.amount}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormInput
                    name="taxDeductible"
                    label="Odp. z dane (€)"
                    type="number"
                    value={formData.taxDeductible}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormInput
                    name="date"
                    label="Dátum výdavku"
                    type="date"
                    value={formData.date}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormInput
                    name="dueDate"
                    label="Dátum splatnosti"
                    type="date"
                    value={formData.dueDate}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormInput
                    name="refNo"
                    label="Referenčné číslo"
                    value={formData.refNo}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              {canEdit && (
                <Button variant="contained" onClick={handleSave} disabled={loading} sx={{ mt: 2 }}>
                  Uložiť zmeny
                </Button>
              )}
            </Box>

            <Divider />

            {/* Unit assignments */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Priradenie k nehnuteľnostiam</Typography>
                {!isLocked && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddAssignmentRow}
                  >
                    Pridať
                  </Button>
                )}
              </Stack>

              {assignmentRows.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Výdavok nie je priradený k žiadnej nehnuteľnosti.
                </Typography>
              )}

              <Stack spacing={1}>
                {assignmentRows.map((row, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Select
                        value={row.unitId}
                        onChange={(e) => handleAssignmentChange(idx, 'unitId', e.target.value)}
                        displayEmpty
                        size="small"
                        disabled={isLocked}
                        sx={{ flex: 1, minWidth: 120 }}
                      >
                        <MenuItem value="">— vybrať —</MenuItem>
                        {availableUnits.map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <TextField
                        value={row.amount}
                        onChange={(e) => handleAssignmentChange(idx, 'amount', e.target.value)}
                        type="number"
                        size="small"
                        disabled={isLocked}
                        label="Suma (€)"
                        inputProps={{ step: '0.01', min: '0.01' }}
                        sx={{ width: 120 }}
                      />
                      <TextField
                        value={app.formatNumber(previewTaxDeductible(idx), 2)}
                        size="small"
                        disabled
                        label="Odp. z dane (€)"
                        sx={{ width: 130 }}
                      />
                      {!isLocked && (
                        <IconButton size="small" onClick={() => handleRemoveAssignmentRow(idx)}>
                          <RemoveItemIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>

              {assignmentRows.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                  <Typography variant="body2" color={assignmentTotalMatchesExpense ? 'success.main' : 'error.main'}>
                    Súčet: {app.formatNumber(assignmentTotal, 2)} € / {app.formatNumber(expenseAmount, 2)} €
                  </Typography>
                  {assignmentTotalMatchesExpense && (
                    <Chip label="OK" color="success" size="small" />
                  )}
                </Stack>
              )}

              {assignmentsEdited && !isLocked && (
                <Button
                  variant="contained"
                  onClick={handleSaveAssignments}
                  disabled={assignmentsLoading}
                  sx={{ mt: 1 }}
                  size="small"
                >
                  Uložiť priradenia
                </Button>
              )}
            </Box>
          </Stack>
        </Grid>

        {/* Right side - Actions, payments and comments */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={2}>
            {/* Action buttons */}
            <Box>
              <Stack spacing={1}>
                <Button
                  onClick={handleDelete}
                  disabled={isLocked}
                  color={isDeleted ? 'primary' : 'error'}
                  variant="outlined"
                  fullWidth
                >
                  {isDeleted ? 'Obnoviť' : 'Zmazať'}
                </Button>
                {!isDeleted && !expense.isCost && (
                  <>
                    {isPaid ? (
                      <Button
                        onClick={() => markUnpaid({ variables: { expenseId: expense.id } })}
                        disabled={isLocked}
                        color="warning"
                        variant="outlined"
                        fullWidth
                      >
                        Označiť ako nezaplatený
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setShowMarkPaidDialog(true)}
                        disabled={isLocked}
                        color="success"
                        variant="outlined"
                        fullWidth
                      >
                        Označiť ako zaplatený
                      </Button>
                    )}
                  </>
                )}
              </Stack>
            </Box>

            {!expense.isCost && (
              <>
                <Divider />

                {/* Payments section */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">Priradené platby</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowPaymentDialog(true)}
                      disabled={isLocked}
                    >
                      Pridať platbu
                    </Button>
                  </Stack>
                  <Box>
                    {isPaid && (
                      <Typography variant="body1" color="success.main">
                        Tento výdavok bol zaplatený dňa {app.formatDate(expense.paidAt)}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Zaplatené: {app.formatNumber(expense.paidAmount, 2)} € z {app.formatNumber(expense.amount, 2)} €
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <ExpensePaymentList payments={expense.payments || []} context="expense" onRemove={handleRemovePayment} />
                  </Box>
                </Box>
              </>
            )}

            <Divider />

            {/* Comments section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Komentáre
              </Typography>
              <InputComment
                label="Pridať komentár"
                disabled={isLocked}
                onSubmit={(comment) => addComment({ variables: { expenseId: expense.id, input: { comment } } })}
                multiline
                clearOnSubmit
              />
              <Box maxHeight="20rem" overflow="auto" sx={{ mt: 2 }}>
                <CommentList
                  disabled={isLocked}
                  comments={expense.comments}
                  onDelete={(c) => removeComment({ variables: { expenseId: expense.id, commentId: c.id } })}
                  onEdit={(commentId, comment) =>
                    updateComment({ variables: { expenseId: expense.id, input: { id: commentId, comment } } })
                  }
                />
              </Box>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {/* Mark as Paid Dialog */}
      {showMarkPaidDialog && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
          onClick={() => setShowMarkPaidDialog(false)}
        >
          <Box
            sx={{ backgroundColor: 'background.paper', borderRadius: 1, p: 3, minWidth: 300, maxWidth: 500 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" gutterBottom>
              Označiť výdavok ako zaplatený
            </Typography>
            <Box sx={{ mt: 2, mb: 3 }}>
              <FormInput
                name="paidDate"
                label="Dátum zaplatenia"
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                required
                variant="outlined"
              />
            </Box>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setShowMarkPaidDialog(false)} disabled={markPaidLoading}>
                Zrušiť
              </Button>
              <Button
                onClick={() =>
                  markPaid({
                    variables: {
                      expenseId: expense.id,
                      paidAt: new Date(paidDate).toISOString(),
                    },
                  })
                }
                variant="contained"
                disabled={markPaidLoading}
              >
                Potvrdiť
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Confirmation dialog for removing payment */}
      {paymentToDelete && (
        <ConfirmationDialog
          open={!!paymentToDelete}
          title="Odstránenie úhrady"
          message="Naozaj chcete odstrániť túto úhradu?"
          onCancel={() => setPaymentToDelete(null)}
          onConfirm={confirmRemovePayment}
          cancelLabel="Zrušiť"
          confirmLabel="Odstrániť"
        />
      )}

      {/* Payment dialog */}
      {showPaymentDialog && expense && (
        <MoneyTransactionDialog
          open={showPaymentDialog}
          transaction={null}
          onClose={() => setShowPaymentDialog(false)}
          onRefresh={() => refetch?.()}
          expenseAssignment={{
            expenseId: expense.id,
            expenseAmount: expense.amount,
            paidAmount: expense.paidAmount,
          }}
        />
      )}
    </BasePage>
  );
}
